import React, { useEffect, useState } from 'react'
import { useLocation } from '@reach/router'
import Helmet from './Helmet'
import { withStore, useStore } from 'react-context-hook'
import { ParallaxProvider } from 'react-scroll-parallax';



import 'normalize.css'

import MessinaSansWeb_Regular from '../fonts/MessinaSansWeb-Regular.woff'
import MessinaSansWeb_Regular_woff2 from '../fonts/MessinaSansWeb-Regular.woff2'

import MessinaSansWeb_SemiBold from '../fonts/MessinaSansWeb-SemiBold.woff'
import MessinaSansWeb_SemiBold_woff2 from '../fonts/MessinaSansWeb-SemiBold.woff2'

import Roman_Grotesque_WEB_Light from '../fonts/Roman_Grotesque_WEB-Light.woff'
import Roman_Grotesque_WEB_Light_woff2 from '../fonts/Roman_Grotesque_WEB-Light.woff2'

import { createGlobalStyle, ThemeProvider } from 'styled-components'
const GlobalStyle = createGlobalStyle`

    @font-face {
        font-family: 'MessinaSans';
        src: url(${MessinaSansWeb_Regular_woff2}) format('woff2');
        font-weight: 400;
        font-style: normal;
    }

    @font-face {
        font-family: 'MessinaSans';
        src: url(${MessinaSansWeb_Regular_woff2}) format('woff2');
        font-weight: 300;
        font-style: normal;
    }

    @font-face {
        font-family: 'MessinaSansBold';
        src: url(${MessinaSansWeb_SemiBold_woff2}) format('woff2');
        font-weight: 600;
        font-style: normal;
    }

    @font-face {
        font-family: 'Roman Grotesque';
        src: url(${Roman_Grotesque_WEB_Light_woff2}) format('woff2');
        font-style: normal;
        font-weight: 300;
    }    


    html {
        background: ${Variables.color.lightGrey};
        font-family: ${Variables.font.body};
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        line-height: 1;
        color: ${Variables.color.cocoa};
    }

    body, html {
        height: 100%;
    }

    body {
        overscroll-behavior: none;
    }

    a {
        text-decoration: none;
        color: ${Variables.color.cocoa};
    }

    ::selection {
        background: ${Variables.color.primary};
        color: white;
    }
    ::-moz-selection {
        background: ${Variables.color.primary};
        color: white;
    }

`

const theme = {
    flexboxgrid: {
        // Defaults
        gridSize: 12, // columns
        gutterWidth: 1, // rem
        outerMargin: 0, // rem
        mediaQuery: 'only screen',
        container: {
            sm: 90, // rem
            md: 90, // rem
            lg: 90, // rem
        },
        breakpoints: {
            xs: 0, // em
            sm: 48, // em
            md: 64, // em
            lg: 75, // em
        },
    },
}

import { getCurrentLangKey, getLangs, getUrlForLang } from 'ptz-i18n'
import { StaticQuery, graphql, navigate } from 'gatsby'
import { IntlProvider } from 'react-intl'
import '@formatjs/intl-pluralrules/polyfill'
import styled, { css } from 'styled-components'
import media from 'css-in-js-media'

import Footer from './Footer'
import Header from './Header'
import { Variables } from '../styles/Variables'
import SmoothScroll from './SmoothScroll'
import LayoutContext from './LayoutContext'
import CookiesNotice from './CookiesNotices'
import { useSiteMetadata } from '../hooks/MetaData'

const Wrapper = styled.div`
    ${(props) =>
        props.stopScroll &&
        css`
            ${media('<desktop')} {
                overflow: hidden;
                height: 100vh;
            }
        `}
`

const Main = styled.main``

const getRedirectLanguage = () => {
    if (typeof navigator === `undefined`) {
        return 'en'
    }

    const lang = navigator && navigator.language && navigator.language.split('-')[0]
    if (!lang) return 'en'

    switch (lang) {
        case 'de':
            return 'de'
        default:
            return 'en'
    }
}

const Layout = ({ children, location }) => {
    return (
        <StaticQuery
            query={graphql`
                query LayoutQuery {
                    site {
                        siteMetadata {
                            languages {
                                defaultLangKey
                                langs
                            }
                        }
                    }
                }
            `}
            render={(data) => {
                const location = useLocation()
                const url = location.pathname
                const LanguageLabels = {
                    'en-GB': { langKeyShort: 'en', labelFull: 'English', labelDesktop: 'EN' },
                    de: { langKeyShort: 'de', labelFull: 'German', labelDesktop: 'DE' },
                }
            
                const [SidebarOpen, setSidebarOpen] = useState(false)
                const [isLanguageMenuOpen, setLanguageMenuOpen] = useState(false)
                const [isGDPR, setGDPR] = useState(false)

                const { langs, defaultLangKey } = useSiteMetadata().languages
                const langKey = getCurrentLangKey(langs, defaultLangKey, url)
                const homeLink = `/${langKey}/`.replace(`/${defaultLangKey}/`, '/')
                const langsMenu = getLangs(langs, langKey, getUrlForLang(homeLink, url)).map((item) => ({
                    ...item,
                    link: item.link.replace(`/${defaultLangKey}/`, '/'),
                    langKeyShort: LanguageLabels[item.langKey].langKeyShort,
                    labelFull: LanguageLabels[item.langKey].labelFull,
                    labelDesktop: LanguageLabels[item.langKey].labelDesktop,
                    prefix: homeLink,
                }))
            
                const CurrentLanguage = langsMenu.find((i: { selected: boolean }) => i.selected == true)
            
                const i18nMessages = require(`../data/messages/${langKey}`)
            
                useEffect(() => {
                    const urlLang = getRedirectLanguage()
                    const LanguageDetectedBefore = localStorage.getItem('PazolaLanguageDetected')
                    if (CurrentLanguage.langKeyShort == 'en' && urlLang != 'en' && !LanguageDetectedBefore) {
                        navigate(`/${urlLang}${url}`, { replace: true })
                        localStorage.setItem('PazolaLanguageDetected', 'yes')
                    }
                }, [])

                return (
                    <IntlProvider locale={langKey} messages={i18nMessages}>
                        <ThemeProvider theme={theme}>
                        <LayoutContext.Provider
                            value={{
                                languageData: langsMenu,
                                currentLang: CurrentLanguage,
                                SidebarOpen,
                                setSidebarOpen,
                                isLanguageMenuOpen,
                                setLanguageMenuOpen,
                                isGDPR,
                                setGDPR,
                            }}
                        >
                            <GlobalStyle />
                            <Helmet
                                title="Pazola"
                                meta={[
                                    { name: 'description', content: '' },
                                    {
                                        name: 'keywords',
                                        content: '',
                                    },
                                ]}
                            />                            
                            <Header />
                            <CookiesNotice />
                            <SmoothScroll>
                                <Wrapper stopScroll={SidebarOpen}>
                                    <Main>{children}</Main>
                                    <Footer/>
                                </Wrapper>
                            </SmoothScroll>
                            </LayoutContext.Provider>                            
                        </ThemeProvider>
                    </IntlProvider>
                )
            }}
        />
    )
}

export default Layout
